import { useParams, Link } from 'react-router-dom'
import {
  useCourseBySlug,
  useCheckEnrollment,
  useEnrollCourse,
  useNextEpisode,
} from '@/hooks/useCourses'
import { useCourseProgress } from '@/hooks/use-progress'
import { useAuth } from '@/hooks'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import ModuleSection from '@/components/ModuleSection'
import FeaturedModuleSection from '@/components/FeaturedModuleSection'
import type { EpisodeProgressMap } from '@/components/FeaturedModuleSection'
import { getEpisodeIcon } from '@/utils/episodeIcons'
import { HtmlContent } from '@/components/ui/html-content'
import { CourseTrailer } from '@/components/CourseTrailer'
import { MasterclassDetailView } from '@/components/MasterclassDetailView'
import { formatDuration } from '@/utils/duration'
import { Loader2, Clock, BookOpen, ArrowLeft, Lock, CheckCircle, Play, Clock3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LevelIcon } from '@/components/ui/LevelIcon'

const CourseDetail = () => {
  const { slug } = useParams<{ slug: string }>()
  const { user, isAuthenticated } = useAuth()
  const { data: course, isLoading, error } = useCourseBySlug(slug || '')
  const { data: isEnrolled } = useCheckEnrollment(course?.id || 0)
  const { data: nextEpisode, isLoading: loadingNextEpisode } = useNextEpisode(course?.id || 0)
  const enrollMutation = useEnrollCourse()

  // Busca progresso do usuário no curso (só se matriculado)
  const { data: courseProgress } = useCourseProgress(isEnrolled ? course?.id : undefined)

  // Monta mapa de progresso: episodeId → { completed, progressPercent }
  const progressMap: EpisodeProgressMap = {}
  if (courseProgress) {
    for (const ep of courseProgress) {
      const percent =
        ep.duration && ep.duration > 0
          ? Math.round((ep.watchTime / ep.duration) * 100)
          : ep.completed
            ? 100
            : 0
      progressMap[ep.episodeId] = {
        completed: ep.completed,
        progressPercent: Math.min(percent, 100),
      }
    }
  }

  // Verifica se todos os episódios foram concluídos (mesma lógica da home page)
  const allEpisodesCompleted =
    isEnrolled &&
    !!courseProgress &&
    !!course &&
    course.modules.length > 0 &&
    course.modules.every(
      (module) =>
        module.episodes.length > 0 && module.episodes.every((ep) => progressMap[ep.id]?.completed)
    )

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
        <Footer />
      </>
    )
  }

  if (error || !course) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-destructive mb-2">Curso não encontrado</h2>
            <p className="text-muted-foreground mb-4">
              O curso que você está procurando não existe
            </p>
            <Link to="/cursos">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para Cursos
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  const handleEnroll = () => {
    if (!isAuthenticated) {
      window.location.href = '/auth'
      return
    }
    enrollMutation.mutate(course.id)
  }

  const courseTypeLabels: Record<string, string> = {
    regular: 'Curso',
    masterclass: 'Masterclass',
    drop: 'Drop',
    workshop: 'Workshop',
    trail: 'Trilha',
  }

  const levelLabels: Record<string, string> = {
    beginner: 'Iniciante',
    intermediate: 'Intermediário',
    advanced: 'Avançado',
    expert: 'Especialista',
  }

  const totalEpisodes = course.modules.reduce(
    (acc, module) => acc + (module.episodes?.length || 0),
    0
  )

  // Masterclass tipicamente é um vídeo único / aula condensada — não
  // faz sentido mostrar "módulos/episódios" como métrica de catálogo
  // (passa a impressão errada de curso longo). Esconde nos blocos meta
  // e "Comece agora". Resto (duração, nível, trailer, conteúdo) fica.
  const isMasterclass = course.type === 'masterclass'

  // `coming_soon`: bloqueia o CTA de assistir mesmo se o usuário já estiver
  // matriculado (auto-enroll por empresa, super_admin, etc.). O trailer
  // segue aparecendo logo abaixo do título.
  const isComingSoon = course.status === 'coming_soon'

  // Masterclass usa página dedicada — layout próprio com cards Mentor +
  // Workbook, sem módulos/episódios/stepper. Early return delegando.
  if (isMasterclass) {
    return (
      <MasterclassDetailView
        course={course as unknown as import('@/types/course').Course}
        isEnrolled={isEnrolled}
        isAuthenticated={isAuthenticated}
        enrollPending={enrollMutation.isPending}
        onEnroll={handleEnroll}
        allEpisodesCompleted={allEpisodesCompleted}
      />
    )
  }

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-background to-muted/20 py-12 md:py-20">
        <div className="container">
          {/* Breadcrumb */}
          <Link
            to="/cursos"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Cursos
          </Link>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Course Info */}
            <div className="lg:col-span-7 space-y-6">
              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary font-medium">
                  {courseTypeLabels[course.type]}
                </span>
                <span className="text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground font-medium">
                  {levelLabels[course.level]}
                </span>
                {course.accessType === 'restricted' && (
                  <span className="text-xs px-3 py-1.5 rounded-full bg-orange-500/10 text-orange-500 font-medium flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Acesso Restrito
                  </span>
                )}
                {isComingSoon && (
                  <span className="text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground font-medium flex items-center gap-1">
                    <Clock3 className="w-3 h-3" />
                    Em breve
                  </span>
                )}
                {isEnrolled && (
                  <span className="text-xs px-3 py-1.5 rounded-full bg-green-500/10 text-green-500 font-medium flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {allEpisodesCompleted ? 'Finalizado' : 'Matriculado'}
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                {course.title}
              </h1>

              {/* Trailer YouTube (opcional — só renderiza se admin cadastrou
                  e a URL casa com algum dos formatos suportados) */}
              {course.trailer && <CourseTrailer url={course.trailer} courseTitle={course.title} />}

              {/* Description */}
              <HtmlContent content={course.description} className="text-lg text-muted-foreground" />

              {/* Meta */}
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                {course.level && (
                  <div className="flex items-center gap-2">
                    <LevelIcon level={course.level} size={18} />
                    <span>
                      {
                        {
                          beginner: 'Iniciante',
                          intermediate: 'Intermediário',
                          advanced: 'Avançado',
                          expert: 'Expert',
                        }[course.level]
                      }
                    </span>
                  </div>
                )}
                {course.duration && course.duration > 0 && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{formatDuration(course.duration)} de conteúdo</span>
                  </div>
                )}
                {!isMasterclass && (
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    <span>
                      {course.modules.length} módulos • {totalEpisodes} episódios
                    </span>
                  </div>
                )}
              </div>

              {/* Category */}
              {course.category && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Categoria: </span>
                  <span className="text-foreground font-medium">{course.category}</span>
                </div>
              )}
            </div>

            {/* Enrollment Card */}
            <div className="lg:col-span-5">
              <div className="bg-card border border-border rounded-lg p-6 space-y-4 sticky top-24">
                <h3 className="font-bold text-xl">Comece agora</h3>

                {isEnrolled && isComingSoon ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock3 className="w-5 h-5" />
                      <span className="font-medium">Curso em breve</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Você já está matriculado. O acesso ao conteúdo será liberado quando o curso
                      for publicado.
                    </p>
                  </div>
                ) : isEnrolled ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-500">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">
                        {allEpisodesCompleted
                          ? 'Você finalizou este curso'
                          : 'Você está matriculado neste curso'}
                      </span>
                    </div>

                    {loadingNextEpisode ? (
                      <Button className="w-full" disabled>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Carregando...
                      </Button>
                    ) : nextEpisode ? (
                      <div className="space-y-2">
                        {!nextEpisode.isFirstEpisode && !allEpisodesCompleted && (
                          <p className="text-sm text-muted-foreground">
                            {`Próximo: ${nextEpisode.moduleTitle} - ${nextEpisode.episodeTitle}`}
                          </p>
                        )}
                        <Button
                          className="w-full"
                          onClick={() => {
                            if (nextEpisode) {
                              window.location.href = `/episode/${nextEpisode.moduleId}/${nextEpisode.episodeNumber}`
                            }
                          }}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {nextEpisode.isFirstEpisode && !allEpisodesCompleted
                            ? 'Começar Curso'
                            : allEpisodesCompleted
                              ? 'Reiniciar curso'
                              : 'Continuar Aprendendo'}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        className="w-full"
                        onClick={() => {
                          const firstModule = course.modules[0]
                          const firstEpisode = firstModule?.episodes[0]
                          if (firstModule && firstEpisode) {
                            window.location.href = `/episode/${firstModule.id}/${firstEpisode.number}`
                          }
                        }}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Começar Curso
                      </Button>
                    )}
                  </div>
                ) : course.accessType === 'restricted' ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-orange-500">
                      <Lock className="w-5 h-5" />
                      <span className="font-medium">Curso com acesso restrito</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Este curso requer aprovação de um administrador para matrícula.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {!isAuthenticated && (
                      <p className="text-sm text-muted-foreground">
                        Faça login para se matricular neste curso
                      </p>
                    )}
                    <Button
                      className="w-full"
                      onClick={handleEnroll}
                      disabled={enrollMutation.isPending}
                    >
                      {enrollMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Matriculando...
                        </>
                      ) : (
                        'Matricular Gratuitamente'
                      )}
                    </Button>
                  </div>
                )}

                {/* Course Stats */}
                <div className="pt-4 border-t border-border space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duração total:</span>
                    <span className="font-medium">{formatDuration(course.duration)}</span>
                  </div>
                  {!isMasterclass && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Módulos:</span>
                        <span className="font-medium">{course.modules.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Episódios:</span>
                        <span className="font-medium">{totalEpisodes}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Nível:</span>
                    <span className="font-medium">{levelLabels[course.level]}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-12">
        <div className="container mb-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Conteúdo do Curso</h2>
          {!isMasterclass && (
            <p className="text-muted-foreground">
              {course.modules.length} módulos • {totalEpisodes} episódios
            </p>
          )}
        </div>

        {course.modules.map((module, index) =>
          isEnrolled ? (
            <FeaturedModuleSection
              key={module.id}
              moduleId={module.id}
              moduleOrder={index + 1}
              title={module.title}
              description={module.description || ''}
              isLatestCourse={true}
              progressMap={progressMap}
              episodes={module.episodes.map((ep) => ({
                id: ep.id,
                number: ep.number,
                title: ep.title,
                icon: getEpisodeIcon(ep.title, index, ep.number),
                thumbnailUrl: ep.thumbnailUrl,
                status: ep.status,
              }))}
            />
          ) : (
            <ModuleSection
              key={module.id}
              moduleId={module.id}
              moduleOrder={index + 1}
              title={module.title}
              description={module.description || ''}
              episodes={module.episodes.map((ep) => ({
                number: ep.number,
                title: ep.title,
                duration: ep.duration ? `${ep.duration} min` : undefined,
                thumbnailUrl: ep.thumbnailUrl,
              }))}
            />
          )
        )}
      </section>

      <Footer />
    </>
  )
}

export default CourseDetail
